import { useState, type FormEvent } from 'react';
import type { TotpSetup } from '../auth/types';
import { errorMessage } from './States';
import { QrCode } from './QrCode';

interface Props {
  setup: TotpSetup;
  onVerify: (code: string) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

/** Shared enrolment UI: QR code, manual key and the confirmation code form. */
export function TotpEnrolment({ setup, onVerify, onCancel, submitLabel = 'Verify and enable' }: Props) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit}>
      <ol className="steps secondary">
        <li>Open an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password, Authy…).</li>
        <li>Scan the QR code, or enter the key by hand.</li>
        <li>Type the 6-digit code the app shows to confirm.</li>
      </ol>
      <div className="totp">
        <QrCode value={setup.uri} label="QR code for your authenticator app" />
        <div className="totp__key">
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowKey((s) => !s)}>
            {showKey ? 'Hide key' : "Can't scan? Show the key"}
          </button>
          {showKey ? <code className="totp__secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code> : null}
        </div>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      <label>
        <div className="small secondary">Code from the app</div>
        <input className="input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required value={code} onChange={(e) => setCode(e.target.value)} style={{ maxWidth: 200 }} />
      </label>
      <div className="row">
        <button type="submit" className="btn btn--primary" disabled={busy || code.trim().length < 6}>{busy ? 'Verifying…' : submitLabel}</button>
        {onCancel ? <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button> : null}
      </div>
    </form>
  );
}
